/* eslint-disable react/prop-types */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button, Card, Col, Form, InputGroup, Modal, Row, Spinner, Table } from 'react-bootstrap';
import { Link, useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import imageTitle from 'assets/img/illustrations/reports-greeting.png';
import useToken from 'hooks/useToken';
import { useToast } from 'context/providers/ToastProvider';
import AdvanceTable from 'components/common/advance-table/AdvanceTable';
import AdvanceTableWrapper from 'components/common/advance-table/AdvanceTableWrapper';
import SkeletonTable from 'components/common/SkeletonTable';
import SoftBadge from 'components/common/SoftBadge';
import IconItem from 'components/common/icon/IconItem';
import FooterPaginacion from 'akasia-components/administracion/usuarios-sistema/FooterPaginacion';
import TitleSelectorSupportDesk from 'akasia-components/dashboards/general/TitleSelectorSupportDesk';
import ModalHistorialPlanFacturacionMx from 'akasia-components/cliente/components/ModalHistorialPlanFacturacionMx';

const RESUMEN_INICIAL = {
    iTotalTimbresUsados: 0,
    iTotalTimbresDisponibles: 0,
    dTotalIngresoMXN: 0,
    dPctInventarioDisponible: 0,
    iTimbresUsadosMesActual: 0,
    iTimbresUsadosMesAnterior: 0,
    iTimbresUsadosMesHace2: 0,
    iTimbresDisponiblesMesActual: 0,
    iTimbresDisponiblesMesAnterior: 0,
    iTimbresDisponiblesMesHace2: 0,
    iTimbresCompradosMesActual: 0,
    iTimbresCompradosMesAnterior: 0,
    iTimbresCompradosMesHace2: 0,
    iCargaInicialMesActual: 0,
    iCargaInicialMesAnterior: 0,
    iCargaInicialMesHace2: 0,
    dTotalMxnMesActual: 0,
    dTotalMxnMesAnterior: 0,
    dTotalMxnMesHace2: 0,
    dTotalMxnCierreMesActual: 0,
    dTotalMxnCierreMesAnterior: 0,
    dTotalMxnCierreMesHace2: 0,
    iTotalPlanesGlobal: 0
};

const parseReporteLista = (detalles) => {
    if (!detalles || typeof detalles !== 'object') {
        return { planes: [], totalRecords: 0 };
    }

    const tables = Object.values(detalles).filter(Array.isArray);
    const planes = tables.find((rows) => rows.some((r) => r?.idPlanFacturaMx != null)) || [];
    const totalRow = tables.flat().find((r) => r?.totalRecords != null);

    return {
        planes,
        totalRecords: totalRow?.totalRecords || 0
    };
};

const parseReporteResumen = (detalles) => {
    if (!detalles || typeof detalles !== 'object') {
        return RESUMEN_INICIAL;
    }

    const tables = Object.values(detalles).filter(Array.isArray);
    return tables.flat().find((r) => r?.iTotalTimbresUsados != null) || RESUMEN_INICIAL;
};

const formatNumero = (value) => Number(value || 0).toLocaleString('es-MX');

const formatMxn = (value) =>
    Number(value || 0).toLocaleString('es-MX', { style: 'currency', currency: 'MXN' });

const formatNumeroCompacto = (value) => {
    const n = Number(value || 0);
    if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
    if (n >= 100_000) return `${Math.round(n / 1000)}k`;
    return formatNumero(n);
};

const formatMxnCompacto = (value) => {
    const n = Number(value || 0);
    if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}M`;
    if (n >= 10_000) return `$${Math.round(n / 1000)}k`;
    if (n >= 1000) return `$${(n / 1000).toFixed(1)}k`;
    return `$${Math.round(n)}`;
};

const formatFechaCorta = (fechaStr) => {
    if (!fechaStr) return null;
    const fecha = new Date(fechaStr);
    if (Number.isNaN(fecha.getTime())) return null;
    return fecha.toLocaleDateString('es-MX', { day: 'numeric', month: 'short', year: 'numeric' });
};

const getEtiquetaRelativa = (fechaStr) => {
    if (!fechaStr) return null;

    const fecha = new Date(fechaStr);
    if (Number.isNaN(fecha.getTime())) return null;

    const hoy = new Date();
    hoy.setHours(0, 0, 0, 0);
    const diaFactura = new Date(fecha);
    diaFactura.setHours(0, 0, 0, 0);

    const diffDays = Math.round((hoy - diaFactura) / (1000 * 60 * 60 * 24));
    if (diffDays <= 0) return 'HOY';
    if (diffDays === 1) return 'AYER';
    return `HACE ${diffDays} DÍAS`;
};

const getSaldoTimbres = (disponibles) => {
    const disp = Number(disponibles || 0);

    if (disp === 0) {
        return { label: 'SIN SALDO', bg: 'secondary' };
    }
    if (disp <= 25) {
        return { label: 'CRÍTICO', bg: 'danger' };
    }
    if (disp <= 200) {
        return { label: 'BAJO', bg: 'warning' };
    }
    return { label: 'NORMAL', bg: 'success' };
};

const calcVariacionMes = (actual, anterior) => {
    const a = Number(actual || 0);
    const b = Number(anterior || 0);
    if (b === 0) return a > 0 ? 100 : 0;
    return ((a - b) / b) * 100;
};

const formatVariacionMes = (variacion) =>
    `${variacion >= 0 ? '+' : ''}${variacion.toFixed(1)}% vs mes anterior`;

const getVariacionClass = (variacion) =>
    variacion >= 0 ? 'text-success' : 'text-danger';

const formatMesAnioTitulo = (fecha) => {
    const etiqueta = fecha.toLocaleDateString('es-MX', { month: 'long', year: 'numeric' });
    return etiqueta.charAt(0).toUpperCase() + etiqueta.slice(1);
};

const getMesesReferencia = () => {
    const mesActual = new Date();
    const mesAnterior = new Date(mesActual.getFullYear(), mesActual.getMonth() - 1, 1);
    const mesHace2 = new Date(mesActual.getFullYear(), mesActual.getMonth() - 2, 1);
    return { mesActual, mesAnterior, mesHace2 };
};

const getEtiquetaTrimestre = () => {
    const { mesHace2, mesActual } = getMesesReferencia();
    const anio = mesActual.getFullYear();
    return `${formatMesCorto(mesHace2)} – ${formatMesCorto(mesActual)} ${anio}`;
};

const buildDatosTrimestre = (resumen) => {
    const { mesActual, mesAnterior, mesHace2 } = getMesesReferencia();
    return [
        {
            mes: mesHace2,
            etiqueta: formatMesAnioTitulo(mesHace2),
            usados: resumen.iTimbresUsadosMesHace2,
            disponiblesCierre: resumen.iTimbresDisponiblesMesHace2,
            comprados: resumen.iTimbresCompradosMesHace2,
            cargaInicial: resumen.iCargaInicialMesHace2,
            ingresoMes: resumen.dTotalMxnMesHace2,
            ingresoCierre: resumen.dTotalMxnCierreMesHace2
        },
        {
            mes: mesAnterior,
            etiqueta: formatMesAnioTitulo(mesAnterior),
            usados: resumen.iTimbresUsadosMesAnterior,
            disponiblesCierre: resumen.iTimbresDisponiblesMesAnterior,
            comprados: resumen.iTimbresCompradosMesAnterior,
            cargaInicial: resumen.iCargaInicialMesAnterior,
            ingresoMes: resumen.dTotalMxnMesAnterior,
            ingresoCierre: resumen.dTotalMxnCierreMesAnterior
        },
        {
            mes: mesActual,
            etiqueta: formatMesAnioTitulo(mesActual),
            usados: resumen.iTimbresUsadosMesActual,
            disponiblesCierre: resumen.iTimbresDisponiblesMesActual,
            comprados: resumen.iTimbresCompradosMesActual,
            cargaInicial: resumen.iCargaInicialMesActual,
            ingresoMes: resumen.dTotalMxnMesActual,
            ingresoCierre: resumen.dTotalMxnCierreMesActual
        }
    ];
};

const formatMesCorto = (fecha) =>
    fecha.toLocaleDateString('es-MX', { month: 'short' }).replaceAll('.', '').trim();

const escapeHtml = (value) => {
    const safeValue = value === null || value === undefined ? '' : String(value);
    return safeValue
        .replaceAll('&', '&amp;')
        .replaceAll('<', '&lt;')
        .replaceAll('>', '&gt;')
        .replaceAll('"', '&quot;');
};

const formatFechaExcel = (fechaStr) => {
    if (!fechaStr) return '';
    const fecha = new Date(fechaStr);
    if (Number.isNaN(fecha.getTime())) return '';
    return fecha.toLocaleString('es-MX');
};

const excelCell = (value, className = 'td') => `<td class="${className}">${escapeHtml(value)}</td>`;

const excelHeaderRow = (cells) =>
    `<tr>${cells.map((c) => `<th class="th">${escapeHtml(c)}</th>`).join('')}</tr>`;

const excelBodyRow = (cells, alt = false) =>
    `<tr>${cells.map((c) => excelCell(c, alt ? 'td-alt' : 'td')).join('')}</tr>`;

const excelSectionTitle = (title, cols = 1) =>
    `<tr><td class="section-title" colspan="${cols}">${escapeHtml(title)}</td></tr>`;

const buildReporteExcelHtml = (resumen, planes, filtroBusqueda) => {
    const resumenGlobal = `
        <table class="inner-table">
            ${excelSectionTitle('Resumen global', 2)}
            ${excelHeaderRow(['Métrica', 'Valor'])}
            ${excelBodyRow(['Total histórico timbres usados', resumen.iTotalTimbresUsados])}
            ${excelBodyRow(['Timbres disponibles hoy', resumen.iTotalTimbresDisponibles], true)}
            ${excelBodyRow(['Ingreso acumulado MXN', resumen.dTotalIngresoMXN])}
            ${excelBodyRow(['Planes activos', resumen.iTotalPlanesGlobal], true)}
        </table>
    `;

    const listadoHeaders = [
        'ID Cliente', 'Cliente', 'RFC', 'Timbres adquiridos',
        'Timbres disponibles', 'Total dinero gastado MXN', 'Última factura'
    ];
    const listadoRows = planes.map((plan, i) => excelBodyRow([
        plan.idClienteAkasia,
        plan.vClienteAkasia,
        plan.vRfc,
        plan.iTimbresAdquiridos,
        plan.iTimbresDisponibles,
        plan.dTotalDineroGastadoMXN,
        formatFechaExcel(plan.dFechaUltimaFactura)
    ], i % 2 === 1)).join('');

    const metaHtml = `
        <table class="inner-table">
            ${excelBodyRow(['Generado', new Date().toLocaleString('es-MX')])}
            ${filtroBusqueda ? excelBodyRow(['Filtro búsqueda', filtroBusqueda], true) : ''}
        </table>
    `;

    return `<!DOCTYPE html>
<html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:x="urn:schemas-microsoft-com:office:excel">
<head>
<meta charset="UTF-8">
<style>
    body { font-family: Calibri, Arial, sans-serif; font-size: 11pt; color: #344050; }
    .main-table { width: 100%; border-collapse: collapse; }
    .inner-table { border-collapse: collapse; width: 100%; }
    .section-title {
        background: #2c7be5;
        color: #ffffff;
        font-weight: bold;
        font-size: 11pt;
        padding: 6px 10px;
        border: 1px solid #2c7be5;
    }
    .th {
        background: #edf2f9;
        color: #344050;
        font-weight: bold;
        padding: 5px 8px;
        border: 1px solid #d8e2ef;
        white-space: nowrap;
    }
    .td {
        background: #ffffff;
        padding: 4px 8px;
        border: 1px solid #d8e2ef;
    }
    .td-alt {
        background: #f9fafd;
        padding: 4px 8px;
        border: 1px solid #d8e2ef;
    }
    .report-title {
        background: #344050;
        color: #ffffff;
        font-size: 14pt;
        font-weight: bold;
        padding: 8px 12px;
        border: 1px solid #344050;
    }
    .spacer td { height: 12px; border: none; background: transparent; }
</style>
</head>
<body>
<table class="main-table">
    <tr><td class="report-title">Reporte Planes Facturación MX</td></tr>
    <tr><td>${metaHtml}</td></tr>
    <tr class="spacer"><td></td></tr>
    <tr><td>${resumenGlobal}</td></tr>
    <tr class="spacer"><td></td></tr>
    <tr><td>
        <table class="inner-table">
            ${excelSectionTitle(`Listado completo de planes (${planes.length})`, listadoHeaders.length)}
            ${excelHeaderRow(listadoHeaders)}
            ${listadoRows}
        </table>
    </td></tr>
</table>
</body>
</html>`;
};

const descargarArchivoExcel = (contenido, nombreArchivo) => {
    const blob = new Blob([contenido], { type: 'application/vnd.ms-excel;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = nombreArchivo;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
};

const MiniComparacionMesesChart = ({ puntos, formatValor = formatNumeroCompacto, formatValorCompleto = formatNumero }) => {
    const coords = puntos.map((punto) => ({
        valor: Number(punto.valor || 0),
        mes: punto.mes
    }));
    const max = Math.max(...coords.map((p) => p.valor), 1);
    const ultimo = coords[coords.length - 1]?.valor ?? 0;
    const penultimo = coords[coords.length - 2]?.valor ?? 0;
    const trendColor = ultimo > penultimo ? '#00d27a' : ultimo < penultimo ? '#e63757' : '#748194';

    const width = 184;
    const height = 102;
    const padX = 16;
    const padTop = 28;
    const padBottom = 22;
    const chartH = height - padTop - padBottom;
    const yBase = padTop + chartH;
    const step = coords.length > 1 ? (width - padX * 2) / (coords.length - 1) : 0;

    const puntosGrafica = coords.map((punto, index) => {
        const x = padX + step * index;
        const y = yBase - (punto.valor / max) * chartH;
        const esUltimo = index === coords.length - 1;
        return { ...punto, x, y, esUltimo };
    });

    const linea = puntosGrafica.map((p) => `${p.x},${p.y}`).join(' ');

    return (
        <svg
            width={width}
            height={height}
            viewBox={`0 0 ${width} ${height}`}
            aria-hidden="true"
            className="flex-shrink-0"
            style={{ overflow: 'visible', minWidth: width }}
        >
            <line x1={padX} y1={yBase} x2={width - padX} y2={yBase} stroke="#d8e2ef" strokeWidth="1" />
            <polyline points={linea} fill="none" stroke={trendColor} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            {puntosGrafica.map((punto, index) => (
                <g key={index}>
                    <title>{formatValorCompleto(punto.valor)}</title>
                    <circle cx={punto.x} cy={punto.y} r="5" fill={punto.esUltimo ? trendColor : '#748194'} />
                    <text
                        x={punto.x}
                        y={punto.y - 10}
                        textAnchor="middle"
                        fontSize={punto.esUltimo ? '12' : '11'}
                        fontWeight="700"
                        fill={punto.esUltimo ? trendColor : '#344050'}
                    >
                        {formatValor(punto.valor)}
                    </text>
                    <text x={punto.x} y={height - 7} textAnchor="middle" fontSize="10" fontWeight="500" fill="#748194">
                        {formatMesCorto(punto.mes)}
                    </text>
                </g>
            ))}
        </svg>
    );
};

const KpiTimbresUsadosCard = ({ resumen }) => {
    const { mesActual, mesAnterior, mesHace2 } = getMesesReferencia();
    const etiquetaMesActual = formatMesAnioTitulo(mesActual);
    const variacion = calcVariacionMes(
        resumen.iTimbresUsadosMesActual,
        resumen.iTimbresUsadosMesAnterior
    );
    const puntosGrafica = [
        { valor: resumen.iTimbresUsadosMesHace2, mes: mesHace2 },
        { valor: resumen.iTimbresUsadosMesAnterior, mes: mesAnterior },
        { valor: resumen.iTimbresUsadosMesActual, mes: mesActual }
    ];

    return (
        <Card className="h-100 shadow-none border">
            <Card.Body className="py-3">
                <div className="d-flex align-items-center mb-2">
                    <IconItem
                        tag="div"
                        icon="ticket-alt"
                        bg="soft-primary"
                        color="primary"
                        size="sm"
                        iconClass="fs-0"
                        className="me-2 shadow-none"
                    />
                    <span className="text-uppercase fs--2 fw-semi-bold text-600">Timbres usados</span>
                </div>
                <div className="d-flex justify-content-between align-items-center gap-2">
                    <div className="flex-grow-1 min-w-0">
                        <p className="fs-3 fw-bold mb-1 text-dark">
                            {formatNumero(resumen.iTimbresUsadosMesActual)}
                        </p>
                        <p className="fs--1 mb-0 text-dark">{etiquetaMesActual}</p>
                        <p className={`fs--2 mb-0 mt-1 ${getVariacionClass(variacion)}`}>
                            {formatVariacionMes(variacion)}
                        </p>
                    </div>
                    <MiniComparacionMesesChart puntos={puntosGrafica} />
                </div>
            </Card.Body>
        </Card>
    );
};

const KpiTimbresDisponiblesCard = ({ resumen }) => {
    const { mesActual, mesAnterior, mesHace2 } = getMesesReferencia();
    const etiquetaMesActual = formatMesAnioTitulo(mesActual);
    const variacion = calcVariacionMes(
        resumen.iTimbresDisponiblesMesActual,
        resumen.iTimbresDisponiblesMesAnterior
    );
    const puntosGrafica = [
        { valor: resumen.iTimbresDisponiblesMesHace2, mes: mesHace2 },
        { valor: resumen.iTimbresDisponiblesMesAnterior, mes: mesAnterior },
        { valor: resumen.iTimbresDisponiblesMesActual, mes: mesActual }
    ];

    return (
        <Card className="h-100 shadow-none border">
            <Card.Body className="py-3">
                <div className="d-flex align-items-center mb-2">
                    <IconItem
                        tag="div"
                        icon="archive"
                        bg="soft-info"
                        color="info"
                        size="sm"
                        iconClass="fs-0"
                        className="me-2 shadow-none"
                    />
                    <span className="text-uppercase fs--2 fw-semi-bold text-600">Timbres disponibles</span>
                </div>
                <div className="d-flex justify-content-between align-items-center gap-2">
                    <div className="flex-grow-1 min-w-0">
                        <p className="fs-3 fw-bold mb-1 text-dark">
                            {formatNumero(resumen.iTotalTimbresDisponibles)}
                        </p>
                        <p className="fs--1 mb-0 text-dark">{etiquetaMesActual}</p>
                        <p className={`fs--2 mb-0 mt-1 ${getVariacionClass(variacion)}`}>
                            {formatVariacionMes(variacion)}
                        </p>
                    </div>
                    <MiniComparacionMesesChart puntos={puntosGrafica} />
                </div>
            </Card.Body>
        </Card>
    );
};

const KpiIngresoCard = ({ resumen }) => {
    const { mesActual, mesAnterior, mesHace2 } = getMesesReferencia();
    const variacion = calcVariacionMes(
        resumen.dTotalMxnMesActual,
        resumen.dTotalMxnMesAnterior
    );
    const puntosGrafica = [
        { valor: resumen.dTotalMxnMesHace2, mes: mesHace2 },
        { valor: resumen.dTotalMxnMesAnterior, mes: mesAnterior },
        { valor: resumen.dTotalMxnMesActual, mes: mesActual }
    ];

    return (
        <Card className="h-100 shadow-none border">
            <Card.Body className="py-3">
                <div className="d-flex align-items-center mb-2">
                    <IconItem
                        tag="div"
                        icon="file-invoice-dollar"
                        bg="soft-success"
                        color="success"
                        size="sm"
                        iconClass="fs-0"
                        className="me-2 shadow-none"
                    />
                    <span className="text-uppercase fs--2 fw-semi-bold text-600">Ingreso generado histórico</span>
                </div>
                <div className="d-flex justify-content-between align-items-center gap-2">
                    <div className="flex-grow-1 min-w-0">
                        <p className="fs-3 fw-bold mb-1 text-dark">
                            {formatMxn(resumen.dTotalIngresoMXN)}
                        </p>
                        <p className={`fs--2 mb-0 mt-1 ${getVariacionClass(variacion)}`}>
                            {formatVariacionMes(variacion)}
                        </p>
                    </div>
                    <MiniComparacionMesesChart
                        puntos={puntosGrafica}
                        formatValor={formatMxnCompacto}
                        formatValorCompleto={formatMxn}
                    />
                </div>
            </Card.Body>
        </Card>
    );
};

const ModalDatosAvanzadosReporte = ({ show, onHide, resumen }) => {
    const meses = buildDatosTrimestre(resumen);
    const sum = (key) => meses.reduce((acc, m) => acc + Number(m[key] || 0), 0);

    const filas = [
        { label: 'Timbres usados', key: 'usados', formato: formatNumero, total: sum('usados') },
        { label: 'Disponibles (cierre)', key: 'disponiblesCierre', formato: formatNumero, total: null },
        { label: 'Timbres comprados', key: 'comprados', formato: formatNumero, total: sum('comprados') },
        { label: 'Carga inicial', key: 'cargaInicial', formato: formatNumero, total: sum('cargaInicial') },
        { label: 'Ingreso del mes', key: 'ingresoMes', formato: formatMxn, total: sum('ingresoMes') },
        { label: 'Ingreso acumulado', key: 'ingresoCierre', formato: formatMxn, total: null }
    ];

    return (
        <Modal show={show} onHide={onHide} centered size="lg">
            <Modal.Header closeButton>
                <Modal.Title>Datos avanzados · {getEtiquetaTrimestre()}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <div className="table-responsive">
                    <Table size="sm" bordered className="fs--1 mb-3">
                        <thead className="bg-light">
                            <tr>
                                <th>Métrica</th>
                                {meses.map((m) => (
                                    <th key={m.etiqueta} className="text-end">{m.etiqueta}</th>
                                ))}
                                <th className="text-end">Total / Actual</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filas.map((fila) => (
                                <tr key={fila.key}>
                                    <td className="fw-semi-bold">{fila.label}</td>
                                    {meses.map((m) => (
                                        <td key={`${fila.key}-${m.etiqueta}`} className="text-end">
                                            {fila.formato(m[fila.key])}
                                        </td>
                                    ))}
                                    <td className="text-end fw-bold">
                                        {fila.total != null
                                            ? fila.formato(fila.total)
                                            : fila.formato(meses[meses.length - 1][fila.key])}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                </div>
                <Row className="g-2">
                    <Col md={4}>
                        <Card className="shadow-none border h-100">
                            <Card.Body className="py-2 px-3">
                                <p className="fs--2 text-muted mb-1">Total histórico usados</p>
                                <p className="fw-bold mb-0">{formatNumero(resumen.iTotalTimbresUsados)}</p>
                            </Card.Body>
                        </Card>
                    </Col>
                    <Col md={4}>
                        <Card className="shadow-none border h-100">
                            <Card.Body className="py-2 px-3">
                                <p className="fs--2 text-muted mb-1">Timbres disponibles hoy</p>
                                <p className="fw-bold mb-0">{formatNumero(resumen.iTotalTimbresDisponibles)}</p>
                            </Card.Body>
                        </Card>
                    </Col>
                    <Col md={4}>
                        <Card className="shadow-none border h-100">
                            <Card.Body className="py-2 px-3">
                                <p className="fs--2 text-muted mb-1">Planes activos</p>
                                <p className="fw-bold mb-0">{formatNumero(resumen.iTotalPlanesGlobal)}</p>
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={onHide}>Cerrar</Button>
            </Modal.Footer>
        </Modal>
    );
};

const ReportePlanesFacturacionMx = () => {
    const { token, setToken, idCatTipoUsuario } = useToken();
    const navigate = useNavigate();
    const { showErrorToast, showSuccessToast } = useToast();

    const [planes, setPlanes] = useState([]);
    const [resumen, setResumen] = useState(RESUMEN_INICIAL);
    const [cargando, setCargando] = useState(true);
    const [cargandoResumen, setCargandoResumen] = useState(true);
    const [searchText, setSearchText] = useState('');
    const [searchDebounced, setSearchDebounced] = useState('');
    const [pageNumber, setPageNumber] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [totalRecords, setTotalRecords] = useState(0);
    const [showModalHistorial, setShowModalHistorial] = useState(false);
    const [planHistorial, setPlanHistorial] = useState(null);
    const [idClienteHistorial, setIdClienteHistorial] = useState(null);
    const [showModalDatosAvanzados, setShowModalDatosAvanzados] = useState(false);
    const [descargandoExcel, setDescargandoExcel] = useState(false);

    const tokenRef = useRef(token);
    tokenRef.current = token;

    useEffect(() => {
        if (idCatTipoUsuario != 1) {
            navigate('/');
        }
    }, [idCatTipoUsuario, navigate]);

    useEffect(() => {
        const timer = setTimeout(() => setSearchDebounced(searchText.trim()), 400);
        return () => clearTimeout(timer);
    }, [searchText]);

    useEffect(() => {
        setPageNumber(1);
    }, [searchDebounced, pageSize]);

    useEffect(() => {
        if (idCatTipoUsuario != 1) return;

        let cancelled = false;

        const cargarResumen = async () => {
            setCargandoResumen(true);

            try {
                const response = await fetch('/api/Administradores/ReportePlanesFacturacionMxResumen', {
                    method: 'POST',
                    headers: {
                        Authorization: 'Bearer ' + tokenRef.current,
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify({})
                });

                if (cancelled) return;

                if (response.status === 401) {
                    setToken(null);
                    navigate('/');
                    return;
                }

                if (!response.ok) {
                    showErrorToast('No se pudo cargar el resumen de planes de facturación.');
                    return;
                }

                const data = await response.json();
                if (data.iResponseType !== 1) {
                    showErrorToast(data.vMessage || 'Error al obtener el resumen.');
                    setResumen(RESUMEN_INICIAL);
                    return;
                }

                const detalles = JSON.parse(data.detalles);
                setResumen(parseReporteResumen(detalles));
            } catch (error) {
                if (!cancelled) {
                    showErrorToast('Error: ' + error.message);
                }
            } finally {
                if (!cancelled) {
                    setCargandoResumen(false);
                }
            }
        };

        cargarResumen();

        return () => {
            cancelled = true;
        };
    }, [idCatTipoUsuario, token]);

    useEffect(() => {
        if (idCatTipoUsuario != 1) return;

        let cancelled = false;

        const cargarLista = async () => {
            setCargando(true);

            try {
                const response = await fetch('/api/Administradores/ReportePlanesFacturacionMx', {
                    method: 'POST',
                    headers: {
                        Authorization: 'Bearer ' + tokenRef.current,
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify({
                        searchText: searchDebounced || null,
                        pageNumber,
                        pageSize
                    })
                });

                if (cancelled) return;

                if (response.status === 401) {
                    setToken(null);
                    navigate('/');
                    return;
                }

                if (!response.ok) {
                    showErrorToast('No se pudo cargar el listado de planes de facturación.');
                    return;
                }

                const data = await response.json();
                if (data.iResponseType !== 1) {
                    showErrorToast(data.vMessage || 'Error al obtener el listado.');
                    setPlanes([]);
                    setTotalRecords(0);
                    return;
                }

                const detalles = JSON.parse(data.detalles);
                const parsed = parseReporteLista(detalles);
                setPlanes(parsed.planes);
                setTotalRecords(parsed.totalRecords);
            } catch (error) {
                if (!cancelled) {
                    showErrorToast('Error: ' + error.message);
                }
            } finally {
                if (!cancelled) {
                    setCargando(false);
                }
            }
        };

        cargarLista();

        return () => {
            cancelled = true;
        };
    }, [idCatTipoUsuario, pageNumber, pageSize, searchDebounced, token]);

    const handleAbrirHistorial = useCallback((row) => {
        setPlanHistorial({
            idPlanFacturaMx: row.idPlanFacturaMx,
            vRfc: row.vRfc,
            iCantidades: row.iTimbresDisponibles
        });
        setIdClienteHistorial(row.idClienteAkasia);
        setShowModalHistorial(true);
    }, []);

    const handleCerrarHistorial = useCallback(() => {
        setShowModalHistorial(false);
        setPlanHistorial(null);
        setIdClienteHistorial(null);
    }, []);

    const handleDescargarReporteExcel = useCallback(async () => {
        setDescargandoExcel(true);

        try {
            const headers = {
                Authorization: 'Bearer ' + tokenRef.current,
                'Content-Type': 'application/json'
            };

            const [resResumen, resLista] = await Promise.all([
                fetch('/api/Administradores/ReportePlanesFacturacionMxResumen', {
                    method: 'POST',
                    headers,
                    body: JSON.stringify({})
                }),
                fetch('/api/Administradores/ReportePlanesFacturacionMx', {
                    method: 'POST',
                    headers,
                    body: JSON.stringify({
                        searchText: searchDebounced || null,
                        pageNumber: 1,
                        pageSize: 999999
                    })
                })
            ]);

            if (resResumen.status === 401 || resLista.status === 401) {
                setToken(null);
                navigate('/');
                return;
            }

            if (!resResumen.ok || !resLista.ok) {
                showErrorToast('No se pudo generar el reporte.');
                return;
            }

            const dataResumen = await resResumen.json();
            const dataLista = await resLista.json();

            if (dataResumen.iResponseType !== 1 || dataLista.iResponseType !== 1) {
                showErrorToast(dataResumen.vMessage || dataLista.vMessage || 'Error al obtener datos del reporte.');
                return;
            }

            const resumenExport = parseReporteResumen(JSON.parse(dataResumen.detalles));
            const { planes: planesExport } = parseReporteLista(JSON.parse(dataLista.detalles));

            if (!planesExport.length) {
                showErrorToast('No hay registros para exportar.');
                return;
            }

            const html = buildReporteExcelHtml(resumenExport, planesExport, searchDebounced);
            const fecha = new Date().toISOString().slice(0, 10);
            descargarArchivoExcel(html, `reporte_planes_facturacion_mx_${fecha}.xls`);
            showSuccessToast(`Reporte descargado (${planesExport.length} planes).`);
        } catch (error) {
            showErrorToast('Error al descargar reporte: ' + error.message);
        } finally {
            setDescargandoExcel(false);
        }
    }, [navigate, searchDebounced, setToken, showErrorToast, showSuccessToast]);

    const columns = useMemo(() => [
        {
            accessor: 'vClienteAkasia',
            Header: 'Cliente Akasia',
            headerClassName: 'text-start bg-light text-uppercase fs--2',
            className: 'text-start',
            Cell: ({ row }) => {
                const { idClienteAkasia, vClienteAkasia } = row.original;
                if (!idClienteAkasia) {
                    return <span className="text-muted">—</span>;
                }
                return (
                    <div>
                        <Link
                            to={`/clientes/detalle/${idClienteAkasia}`}
                            className="fw-semi-bold text-dark text-decoration-none"
                        >
                            {vClienteAkasia || '—'}
                        </Link>
                        <div className="text-muted fs--2">ID: {idClienteAkasia}</div>
                    </div>
                );
            }
        },
        {
            accessor: 'vRfc',
            Header: 'RFC Registrado',
            headerClassName: 'text-start bg-light text-uppercase fs--2',
            className: 'text-start font-monospace',
            Cell: ({ row }) => {
                const { vRfc } = row.original;
                return (
                    <button
                        type="button"
                        className="btn btn-link p-0 fw-bold text-dark text-decoration-none border-0 font-monospace"
                        onClick={() => handleAbrirHistorial(row.original)}
                        title="Ver historial del plan"
                    >
                        {vRfc || '—'}
                    </button>
                );
            }
        },
        {
            accessor: 'iTimbresAdquiridos',
            Header: 'Timbres Adquiridos',
            headerClassName: 'text-center bg-light text-uppercase fs--2',
            className: 'text-center fw-semi-bold',
            Cell: ({ value }) => formatNumero(value)
        },
        {
            accessor: 'iTimbresDisponibles',
            Header: 'Timbres Disponibles',
            headerClassName: 'text-center bg-light text-uppercase fs--2',
            className: 'text-center',
            Cell: ({ row }) => {
                const { iTimbresDisponibles } = row.original;
                const saldo = getSaldoTimbres(iTimbresDisponibles);
                return (
                    <div className="d-flex flex-column align-items-center gap-1">
                        <span className="fw-bold">{formatNumero(iTimbresDisponibles)}</span>
                        <SoftBadge bg={saldo.bg} className="fs--2">{saldo.label}</SoftBadge>
                    </div>
                );
            }
        },
        {
            accessor: 'dTotalDineroGastadoMXN',
            Header: 'Total Dinero Gastado',
            headerClassName: 'text-center bg-light text-uppercase fs--2',
            className: 'text-center fw-semi-bold',
            Cell: ({ value }) => formatMxn(value)
        },
        {
            accessor: 'dFechaUltimaFactura',
            Header: 'Última Factura',
            headerClassName: 'text-start bg-light text-uppercase fs--2',
            className: 'text-start',
            Cell: ({ value }) => {
                const etiqueta = getEtiquetaRelativa(value);
                const fecha = formatFechaCorta(value);

                if (!etiqueta) {
                    return <span className="text-muted">Sin facturas</span>;
                }

                return (
                    <div className="d-flex flex-column align-items-start gap-1">
                        <SoftBadge bg={etiqueta === 'HOY' ? 'info' : 'secondary'} className="fs--2">
                            {etiqueta}
                        </SoftBadge>
                        {fecha && <span className="text-muted fs--2">{fecha}</span>}
                    </div>
                );
            }
        }
    ], [handleAbrirHistorial]);

    return (
        <>
            <Row className="g-3">
                <Col xxl={12}>
                    <Row className="g-0 h-100">
                        <Col xs={12}>
                            <TitleSelectorSupportDesk
                                title1="Planes de Facturación"
                                title2="México"
                                barChart={imageTitle}
                                barChartWidth={72}
                                barChartClassName="ms-n2"
                                actions={
                                    <div className="d-flex flex-wrap gap-2 justify-content-end">
                                        <Button
                                            variant="outline-success"
                                            size="sm"
                                            onClick={handleDescargarReporteExcel}
                                            disabled={descargandoExcel || cargandoResumen}
                                        >
                                            {descargandoExcel ? (
                                                <>
                                                    <Spinner animation="border" size="sm" className="me-1" />
                                                    Generando...
                                                </>
                                            ) : (
                                                'Descargar reporte en Excel'
                                            )}
                                        </Button>
                                        <Button
                                            variant="outline-primary"
                                            size="sm"
                                            onClick={() => setShowModalDatosAvanzados(true)}
                                            disabled={cargandoResumen}
                                        >
                                            Datos avanzados
                                        </Button>
                                    </div>
                                }
                            />
                        </Col>
                    </Row>
                </Col>
            </Row>
            

            <Row className={`g-3 mb-2${cargandoResumen ? ' opacity-50' : ''}`}>
                <Col md={4}>
                    <KpiTimbresUsadosCard resumen={resumen} />
                </Col>
                <Col md={4}>
                    <KpiTimbresDisponiblesCard resumen={resumen} />
                </Col>
                <Col md={4}>
                    <KpiIngresoCard resumen={resumen} />
                </Col>
            </Row>

            <ModalDatosAvanzadosReporte
                show={showModalDatosAvanzados}
                onHide={() => setShowModalDatosAvanzados(false)}
                resumen={resumen}
            />

            <InputGroup className="mb-2">
                <InputGroup.Text className="bg-white">
                    <FontAwesomeIcon icon="search" className="text-400" />
                </InputGroup.Text>
                <Form.Control
                    type="search"
                    className="border-start-0"
                    placeholder="Buscar por RFC o Nombre del Cliente..."
                    value={searchText}
                    onChange={(e) => setSearchText(e.target.value)}
                    aria-label="Buscar plan de facturación"
                />
            </InputGroup>

            <Card className="shadow-none border">
                <Card.Header className="bg-white border-bottom d-flex justify-content-between align-items-center py-3">
                    <Card.Title className="mb-0 fs-1">Listado de Facturación</Card.Title>
                    {totalRecords > 0 && (
                        <span className="text-muted fs--1">
                            {((pageNumber - 1) * pageSize) + 1}–{Math.min(pageNumber * pageSize, totalRecords)} de {totalRecords}
                        </span>
                    )}
                </Card.Header>
                <Card.Body className="p-0">
                    {cargando ? (
                        <div className="p-3">
                            <SkeletonTable columnNames={columns.map(c => c.Header)} rows={pageSize} />
                        </div>
                    ) : (
                        <AdvanceTableWrapper columns={columns} data={planes} sortable pagination={false}>
                            <AdvanceTable
                                table
                                headerClassName="bg-200 text-900 text-nowrap align-middle"
                                rowClassName="align-middle white-space-nowrap"
                                tableProps={{
                                    size: 'sm',
                                    responsive: true,
                                    className: 'fs--1 mb-0'
                                }}
                            />
                        </AdvanceTableWrapper>
                    )}

                    {!cargando && planes.length === 0 && (
                        <p className="text-center text-muted py-4 mb-0">No se encontraron planes de facturación.</p>
                    )}
                </Card.Body>
                <Card.Footer className="bg-light border-top">
                    <Row className="align-items-center g-2">
                        <FooterPaginacion
                            pageSize={pageSize}
                            totalRecords={totalRecords}
                            pageNumber={pageNumber}
                            onPageSizeChange={(e) => setPageSize(Number(e.target.value))}
                            onNextPage={() => setPageNumber(pageNumber + 1)}
                            onPreviousPage={() => setPageNumber(pageNumber - 1)}
                            onGotoPage={(page) => setPageNumber(page + 1)}
                        />
                    </Row>
                </Card.Footer>
            </Card>

            <ModalHistorialPlanFacturacionMx
                show={showModalHistorial}
                onHide={handleCerrarHistorial}
                idClienteAkasia={idClienteHistorial}
                plan={planHistorial}
            />
        </>
    );
};

export default ReportePlanesFacturacionMx;
